import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API_URL } from "../../../setup/config/env";
import BrandedLoading from "../BrandedLoading";
import {
  Box,
  Card,
  Typography,
  Chip,
  Button,
  TextField,
  MenuItem,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid,
  InputAdornment,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  MailOutline as MailIcon,
  CheckCircleOutline as CheckIcon,
  CancelOutlined as CancelIcon,
  ScheduleOutlined as ScheduleIcon,
  TouchAppOutlined as ClickIcon,
  VisibilityOutlined as OpenIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  InfoOutlined as InfoIcon,
  AccountTreeOutlined as WorkflowIcon,
  SendOutlined as DirectIcon,
  Close as CloseIcon,
  AlternateEmail as EmailAtIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";

const BRAND_BLUE = "#0d3559";
const BRAND_ORANGE = "#ea580c";
const BRAND_ORANGE_LIGHT = "#fff7ed";
const BRAND_ORANGE_BORDER = "#fed7aa";

/**
 * Status display mapping with clean Datumart aesthetic tokens
 */
const getStatusConfig = (unifiedStatus) => {
  const status = (unifiedStatus || "sent").toLowerCase();

  switch (status) {
    case "clicked":
      return {
        label: "Clicked",
        icon: <ClickIcon sx={{ fontSize: 15, mr: 0.5 }} />,
        color: "#7c3aed",
        bgColor: "#faf5ff",
        borderColor: "#e9d5ff",
      };
    case "opened":
      return {
        label: "Opened",
        icon: <OpenIcon sx={{ fontSize: 15, mr: 0.5 }} />,
        color: "#0284c7",
        bgColor: "#f0f9ff",
        borderColor: "#bae6fd",
      };
    case "delivered":
      return {
        label: "Delivered",
        icon: <CheckIcon sx={{ fontSize: 15, mr: 0.5 }} />,
        color: "#16a34a",
        bgColor: "#f0fdf4",
        borderColor: "#bbf7d0",
      };
    case "sent":
      return {
        label: "Sent",
        icon: <CheckIcon sx={{ fontSize: 15, mr: 0.5 }} />,
        color: "#16a34a",
        bgColor: "#f0fdf4",
        borderColor: "#bbf7d0",
      };
    case "bounced":
      return {
        label: "Bounced",
        icon: <CancelIcon sx={{ fontSize: 15, mr: 0.5 }} />,
        color: BRAND_ORANGE,
        bgColor: BRAND_ORANGE_LIGHT,
        borderColor: BRAND_ORANGE_BORDER,
      };
    case "failed":
    case "dropped":
    case "spamreport":
      return {
        label: status === "spamreport" ? "Spam" : status === "dropped" ? "Dropped" : "Failed",
        icon: <CancelIcon sx={{ fontSize: 15, mr: 0.5 }} />,
        color: "#dc2626",
        bgColor: "#fef2f2",
        borderColor: "#fecaca",
      };
    case "processed":
    case "processing":
    case "deferred":
    case "pending":
      return {
        label: (status === "processed" || status === "processing") ? "Processed" : status === "deferred" ? "Deferred" : "Pending",
        icon: <ScheduleIcon sx={{ fontSize: 15, mr: 0.5 }} />,
        color: "#d97706",
        bgColor: "#fffbeb",
        borderColor: "#fde68a",
      };
    default:
      return {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        icon: <ScheduleIcon sx={{ fontSize: 15, mr: 0.5 }} />,
        color: "#475569",
        bgColor: "#f8fafc",
        borderColor: "#e2e8f0",
      };
  }
};

const UserEmailHistory = ({ email, userId, userName }) => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, delivered: 0, opened: 0, clicked: 0, failed: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Selected Log Details Dialog
  const [selectedLog, setSelectedLog] = useState(null);

  // Expanded Accordions State - closed by default
  const [expandedWorkflows, setExpandedWorkflows] = useState({});

  const fetchEmailHistory = useCallback(async () => {
    if (!email && !userId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = {
        page,
        limit: 20,
        email: email || undefined,
        userId: userId || undefined,
        search: searchTerm || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        sourceType: sourceFilter !== "all" ? sourceFilter : undefined,
      };

      const res = await axios.get(`${API_URL}/api/campaign/user-email-history`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (res.data && res.data.success) {
        setLogs(res.data.data.list || []);
        setStats(res.data.data.stats || { total: 0, sent: 0, delivered: 0, opened: 0, clicked: 0, failed: 0 });
        setTotalPages(res.data.data.totalPages || 1);
      }
    } catch (err) {
      console.error("Failed to fetch email history:", err);
    } finally {
      setLoading(false);
    }
  }, [email, userId, page, searchTerm, sourceFilter, statusFilter]);

  useEffect(() => {
    fetchEmailHistory();
  }, [fetchEmailHistory]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const toggleAccordion = (id) => {
    setExpandedWorkflows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Group logs so that steps from the same workflow are rendered inside a single unified workflow accordion
  const groupedItems = useMemo(() => {
    const groups = [];
    const workflowMap = new Map();

    logs.forEach((item) => {
      if (item.sourceType === "workflow" && (item.workflowId || item.workflowName)) {
        const wfKey = (item.workflowId || item.workflowName).toString();
        if (!workflowMap.has(wfKey)) {
          const wfGroup = {
            id: `wf_${wfKey}`,
            isWorkflowGroup: true,
            type: "workflow",
            workflowId: item.workflowId,
            workflowName: item.workflowName || item.sourceName || "Workflow",
            workflowContext: item.workflowContext,
            steps: [],
            latestSentAt: item.sentAt,
          };
          workflowMap.set(wfKey, wfGroup);
          groups.push(wfGroup);
        }
        const wfGroup = workflowMap.get(wfKey);
        wfGroup.steps.push(item);
        if (new Date(item.sentAt).getTime() > new Date(wfGroup.latestSentAt).getTime()) {
          wfGroup.latestSentAt = item.sentAt;
        }
      } else {
        groups.push({
          id: item._id || `direct_${Math.random()}`,
          isWorkflowGroup: false,
          type: "direct",
          data: item,
          latestSentAt: item.sentAt,
        });
      }
    });

    // Sort steps within each workflow by stepNo ascending
    workflowMap.forEach((wfGroup) => {
      wfGroup.steps.sort((a, b) => {
        const numA = typeof a.stepNo === "number" ? a.stepNo : parseInt(a.stepNo, 10) || 0;
        const numB = typeof b.stepNo === "number" ? b.stepNo : parseInt(b.stepNo, 10) || 0;
        return numA - numB;
      });
    });

    return groups;
  }, [logs]);

  const openRatePercent = stats.total > 0 ? Math.round((stats.opened / stats.total) * 100) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Clean & Neat Stat Bar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          p: 1.5,
          px: 2,
          mb: 2.5,
          bgcolor: "#f8fafc",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 700, color: BRAND_BLUE, mr: 0.5, fontSize: "14px" }}>
            Summary:
          </Typography>
          <Chip
            label={`Total: ${stats.total}`}
            size="medium"
            sx={{ fontWeight: 600, bgcolor: "#ffffff", border: "1px solid #cbd5e1", color: BRAND_BLUE, height: 26, fontSize: "12.5px" }}
          />
          <Chip
            label={`Delivered: ${stats.delivered}`}
            size="medium"
            sx={{ fontWeight: 600, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a", height: 26, fontSize: "12.5px" }}
          />
          <Chip
            label={`Opened: ${stats.opened}`}
            size="medium"
            sx={{ fontWeight: 600, bgcolor: "#f0f9ff", border: "1px solid #bae6fd", color: "#0284c7", height: 26, fontSize: "12.5px" }}
          />
          <Chip
            label={`Clicked: ${stats.clicked}`}
            size="medium"
            sx={{ fontWeight: 600, bgcolor: "#faf5ff", border: "1px solid #e9d5ff", color: "#7c3aed", height: 26, fontSize: "12.5px" }}
          />
          {stats.failed > 0 && (
            <Chip
              label={`Failed: ${stats.failed}`}
              size="medium"
              sx={{ fontWeight: 600, bgcolor: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", height: 26, fontSize: "12.5px" }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#64748b", fontSize: "13.5px" }}>
            Open Rate: <span style={{ color: BRAND_ORANGE, fontWeight: 700 }}>{openRatePercent}%</span>
          </Typography>
        </Box>
      </Box>

      {/* Filter and Search Bar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", width: { xs: "100%", sm: "auto" }, flex: 1 }}>
          <TextField
            size="small"
            placeholder="Search template name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8", fontSize: 19 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 240 },
              "& .MuiOutlinedInput-root": { borderRadius: "6px", height: 38, fontSize: "13.5px", bgcolor: "#fff" },
            }}
          />

          <TextField
            select
            size="small"
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setPage(1);
            }}
            sx={{
              minWidth: 140,
              "& .MuiOutlinedInput-root": { borderRadius: "6px", height: 38, fontSize: "13.5px", bgcolor: "#fff" },
            }}
          >
            <MenuItem value="all" sx={{ fontSize: "13.5px" }}>All Channels</MenuItem>
            <MenuItem value="workflow" sx={{ fontSize: "13.5px" }}>Workflows</MenuItem>
            <MenuItem value="direct" sx={{ fontSize: "13.5px" }}>Direct / CRM</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            sx={{
              minWidth: 140,
              "& .MuiOutlinedInput-root": { borderRadius: "6px", height: 38, fontSize: "13.5px", bgcolor: "#fff" },
            }}
          >
            <MenuItem value="all" sx={{ fontSize: "13.5px" }}>All Statuses</MenuItem>
            <MenuItem value="opened" sx={{ fontSize: "13.5px" }}>Opened / Clicked</MenuItem>
            <MenuItem value="delivered" sx={{ fontSize: "13.5px" }}>Delivered</MenuItem>
            <MenuItem value="sent" sx={{ fontSize: "13.5px" }}>Sent</MenuItem>
            <MenuItem value="failed" sx={{ fontSize: "13.5px" }}>Failed / Bounced</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ ml: "auto" }}>
          <Tooltip title="Refresh History">
            <span>
              <IconButton
                onClick={fetchEmailHistory}
                disabled={loading}
                size="small"
                sx={{
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  color: BRAND_BLUE,
                  p: "7px",
                  "&:hover": { bgcolor: "rgba(13,53,89,0.04)" },
                }}
              >
                <RefreshIcon sx={{ fontSize: 20, animation: loading ? "spin 1s linear infinite" : "none" }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* History Items List (Workflows rendered as Accordions, closed by default) */}
      {loading ? (
        <Box sx={{ py: 6, textAlign: "center", display: "flex", justifyContent: "center" }}>
          <BrandedLoading />
        </Box>
      ) : groupedItems.length === 0 ? (
        <Box
          sx={{
            py: 5,
            textAlign: "center",
            border: "1px dashed #cbd5e1",
            borderRadius: "8px",
            bgcolor: "#f8fafc",
          }}
        >
          <MailIcon sx={{ fontSize: 40, color: "#94a3b8", mb: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "15px" }}>
            No email interactions recorded
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 380, display: "block", mx: "auto", mt: 0.5, fontSize: "13px" }}>
            {email || userName ? `No emails sent to ${userName || email} yet.` : "Select a contact to view history."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {groupedItems.map((group) => {
            if (group.isWorkflowGroup) {
              const isExpanded = Boolean(expandedWorkflows[group.id]);

              return (
                <Accordion
                  key={group.id}
                  expanded={isExpanded}
                  onChange={() => toggleAccordion(group.id)}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px !important",
                    overflow: "hidden",
                    bgcolor: "#ffffff",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                    "&:before": { display: "none" },
                    "&:hover": { borderColor: "#cbd5e1" },
                  }}
                >
                  {/* Workflow Accordion Header / Summary */}
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: BRAND_BLUE, fontSize: 22 }} />}
                    sx={{
                      p: 1.5,
                      px: 2,
                      minHeight: 52,
                      bgcolor: isExpanded ? "rgba(13, 53, 89, 0.04)" : "rgba(13, 53, 89, 0.02)",
                      borderBottom: isExpanded ? "1px solid #e2e8f0" : "none",
                      "& .MuiAccordionSummary-content": {
                        m: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1.2,
                        pr: 1,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "6px",
                          bgcolor: BRAND_ORANGE_LIGHT,
                          color: BRAND_ORANGE,
                          border: `1px solid ${BRAND_ORANGE_BORDER}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <WorkflowIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "14.5px" }}>
                          {group.workflowName}
                        </Typography>
                        <Chip
                          label="Workflow"
                          size="small"
                          sx={{
                            bgcolor: BRAND_ORANGE_LIGHT,
                            color: BRAND_ORANGE,
                            border: `1px solid ${BRAND_ORANGE_BORDER}`,
                            fontWeight: 700,
                            fontSize: "11.5px",
                            height: 22,
                          }}
                        />
                        {group.workflowContext && (
                          <Chip
                            label={group.workflowContext}
                            size="small"
                            sx={{ bgcolor: "#f1f5f9", color: "#475569", fontSize: "11.5px", height: 22 }}
                          />
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: "auto", mr: 1 }}>
                      <Chip
                        label={`${group.steps.length} ${group.steps.length === 1 ? "step" : "steps"}`}
                        size="small"
                        sx={{
                          bgcolor: "rgba(13, 53, 89, 0.06)",
                          color: BRAND_BLUE,
                          fontWeight: 700,
                          fontSize: "12px",
                          height: 22,
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "#64748b", fontSize: "12.5px", display: { xs: "none", sm: "inline" } }}>
                        Latest: {dayjs(group.latestSentAt).format("DD MMM YYYY, hh:mm A")}
                      </Typography>
                    </Box>
                  </AccordionSummary>

                  {/* Workflow Steps Accordion Details */}
                  <AccordionDetails sx={{ p: 0 }}>
                    {group.steps.map((step, sIdx) => {
                      const statusConf = getStatusConfig(step.unifiedStatus);

                      return (
                        <Box
                          key={step._id || sIdx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            py: 1.4,
                            px: 2,
                            pl: { xs: 2, sm: 3.5 },
                            borderBottom: sIdx < group.steps.length - 1 ? "1px solid #f1f5f9" : "none",
                            bgcolor: "#ffffff",
                            flexWrap: "wrap",
                            gap: 1.2,
                            "&:hover": { bgcolor: "#f8fafc" },
                          }}
                        >
                          {/* Step Badge & Subject */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 220, flex: 1 }}>
                            <Chip
                              label={`Step ${step.stepNo !== undefined ? step.stepNo : sIdx + 1}`}
                              size="small"
                              sx={{
                                bgcolor: "rgba(13, 53, 89, 0.06)",
                                color: BRAND_BLUE,
                                fontWeight: 700,
                                fontSize: "11.5px",
                                borderRadius: "4px",
                                height: 22,
                              }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1e293b",
                                  fontSize: "13.5px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {step.templateName || `Step ${step.stepNo !== undefined ? step.stepNo : sIdx + 1} Email`}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Status Badge */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 100 }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1.1,
                                py: 0.3,
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: statusConf.color,
                                bgcolor: statusConf.bgColor,
                                border: `1px solid ${statusConf.borderColor}`,
                              }}
                            >
                              {statusConf.icon}
                              {statusConf.label}
                            </Box>
                          </Box>

                          {/* Timestamp */}
                          <Box sx={{ minWidth: 130 }}>
                            <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, fontSize: "12.5px" }}>
                              {step.sentAt ? dayjs(step.sentAt).format("DD MMM YYYY, hh:mm A") : "-"}
                            </Typography>
                          </Box>

                          {/* Actions */}
                          <Box>
                            <Tooltip title="View Details">
                              <IconButton size="small" onClick={() => setSelectedLog(step)} sx={{ color: BRAND_BLUE, p: "5px" }}>
                                <InfoIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            }

            // Direct / CRM standalone email card
            const directItem = group.data;
            const statusConf = getStatusConfig(directItem.unifiedStatus);

            return (
              <Card
                key={group.id}
                variant="outlined"
                sx={{
                  borderRadius: "8px",
                  p: 1.5,
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.2,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                  "&:hover": { borderColor: "#cbd5e1" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 220, flex: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "6px",
                      bgcolor: "rgba(13, 53, 89, 0.06)",
                      color: BRAND_BLUE,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <DirectIcon sx={{ fontSize: 17 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: BRAND_BLUE,
                          fontSize: "14px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {directItem.templateName || "Direct Email"}
                      </Typography>
                      <Chip
                        label={directItem.sourceType === "direct" ? "Direct Email" : (directItem.sourceName || "Direct Email")}
                        size="small"
                        sx={{
                          bgcolor: "rgba(13, 53, 89, 0.04)",
                          color: BRAND_BLUE,
                          borderColor: "#cbd5e1",
                          fontWeight: 600,
                          fontSize: "11.5px",
                          height: 22,
                        }}
                        variant="outlined"
                      />
                    </Box>
                    {directItem.toEmail && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mt: 0.2 }}>
                        <Typography variant="caption" sx={{ color: "#475569", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: 0.4 }}>
                          <span style={{ color: "#94a3b8" }}>To:</span> {directItem.toEmail}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Status Badge */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 100 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1.1,
                      py: 0.3,
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: statusConf.color,
                      bgcolor: statusConf.bgColor,
                      border: `1px solid ${statusConf.borderColor}`,
                    }}
                  >
                    {statusConf.icon}
                    {statusConf.label}
                  </Box>
                </Box>

                {/* Timestamp */}
                <Box sx={{ minWidth: 130 }}>
                  <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, fontSize: "12.5px" }}>
                    {directItem.sentAt ? dayjs(directItem.sentAt).format("DD MMM YYYY, hh:mm A") : "-"}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => setSelectedLog(directItem)} sx={{ color: BRAND_BLUE, p: "5px" }}>
                      <InfoIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            size="medium"
            sx={{
              "& .Mui-selected": { bgcolor: `${BRAND_BLUE} !important`, color: "#fff" },
            }}
          />
        </Box>
      )}

      {/* Email Delivery Details Modal */}
      <Dialog
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "10px", p: 0 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: BRAND_BLUE,
            color: "#ffffff",
            py: 1.5,
            px: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmailAtIcon sx={{ fontSize: 22, color: BRAND_ORANGE }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#ffffff", fontSize: "15px" }}>
              Email Delivery Details
            </Typography>
          </Box>
          <IconButton onClick={() => setSelectedLog(null)} size="small" sx={{ color: "#ffffff" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          {selectedLog && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px", display: "block" }}>
                    Template Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: BRAND_BLUE, fontSize: "14.5px" }}>
                    {selectedLog.templateName || "Direct Email"}
                  </Typography>
                </Box>
                {(() => {
                  const conf = getStatusConfig(selectedLog.unifiedStatus);
                  return (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.2,
                        py: 0.4,
                        borderRadius: "4px",
                        fontSize: "12px",
                        fontWeight: 700,
                        color: conf.color,
                        bgcolor: conf.bgColor,
                        border: `1px solid ${conf.borderColor}`,
                      }}
                    >
                      {conf.icon}
                      {conf.label}
                    </Box>
                  );
                })()}
              </Box>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px", display: "block" }}>
                    Recipient Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "13.5px", color: "#1e293b" }}>
                    {selectedLog.toEmail || selectedLog.recipientEmail || email || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px", display: "block" }}>
                    Channel / Source
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "13.5px", color: "#1e293b" }}>
                    {selectedLog.sourceType === "workflow" ? `Workflow (${selectedLog.workflowName || selectedLog.sourceName})` : "Direct Email"}
                  </Typography>
                </Grid>


                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px", display: "block" }}>
                    Date & Time
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "13.5px", color: "#1e293b" }}>
                    {selectedLog.sentAt ? dayjs(selectedLog.sentAt).format("DD MMM YYYY, hh:mm:ss A") : "-"}
                  </Typography>
                </Grid>

                {selectedLog.openCount !== undefined && selectedLog.openCount > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px", display: "block" }}>
                      Total Opens
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, fontSize: "13.5px", color: BRAND_ORANGE }}>
                      {selectedLog.openCount} {selectedLog.openCount === 1 ? "time" : "times"}
                    </Typography>
                  </Grid>
                )}

                {selectedLog.clickCount !== undefined && selectedLog.clickCount > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "12px", display: "block" }}>
                      Total Clicks
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, fontSize: "13.5px", color: "#7c3aed" }}>
                      {selectedLog.clickCount} {selectedLog.clickCount === 1 ? "click" : "clicks"}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* Event Timeline */}
              {selectedLog.events && selectedLog.events.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mb: 0.8, fontSize: "12.5px" }}>
                    Engagement Timeline
                  </Typography>
                  <Box sx={{ bgcolor: "#f8fafc", p: 1.5, borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                    {selectedLog.events.map((ev, idx) => (
                      <Box key={idx} sx={{ display: "flex", justifyContent: "space-between", py: 0.4 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND_BLUE, textTransform: "capitalize", fontSize: "13px" }}>
                          • {ev.event || ev.status}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b", fontSize: "12.5px" }}>
                          {ev.timestamp ? dayjs(ev.timestamp).format("DD MMM, hh:mm A") : "-"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Failure / Reason */}
              {selectedLog.reason && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: "#fef2f2", borderRadius: "6px", border: "1px solid #fecaca" }}>
                  <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 700, display: "block", fontSize: "12.5px" }}>
                    Failure Reason:
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#991b1b", fontSize: "13px", mt: 0.3 }}>
                    {selectedLog.reason}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 1.5, px: 2.5, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={() => setSelectedLog(null)}
            size="medium"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: BRAND_BLUE,
              fontSize: "13.5px",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserEmailHistory;
